/**
 * A stored UTC timestamp, shown to a person in their own timezone (DESIGN.md §4.5): the JSX
 * owner of `lib/datetime`. Never a raw ISO string or an ad-hoc `toLocaleString`.
 */

import { EMPTY } from '~/lib/format';
import {
  DATE_OPTIONS,
  DATE_TIME_OPTIONS,
  formatLocalTime,
  toDate,
  type TimeValue,
} from '~/lib/datetime';

interface LocalTimeProps {
  value: TimeValue;
  /** `datetime` (default) or `date`. */
  format?: 'datetime' | 'date';
  /** Overrides `format` with any `Intl.DateTimeFormatOptions`. */
  options?: Intl.DateTimeFormatOptions;
  className?: string;
  testId?: string;
}

export function LocalTime({
  value,
  format = 'datetime',
  options,
  className,
  testId,
}: LocalTimeProps) {
  const date = toDate(value);
  if (!date) {
    return (
      <span
        className={
          className
            ? `${className} text-gray-400 dark:text-gray-500`
            : 'text-gray-400 dark:text-gray-500'
        }
        data-testid={testId}
      >
        {EMPTY}
      </span>
    );
  }
  const text = formatLocalTime(
    date,
    options ?? (format === 'date' ? DATE_OPTIONS : DATE_TIME_OPTIONS),
  );
  return (
    <time
      dateTime={date.toISOString()}
      className={className}
      title={formatLocalTime(date, { ...DATE_TIME_OPTIONS, timeZoneName: 'short' })}
      data-testid={testId}
    >
      {text}
    </time>
  );
}
